import { useEffect, useId, useState } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";

import "./ErrorPage.css";
import { logError } from "./firebase/analytics";
import { isStaleBuildError, reloadForNewBuild, reloadPage } from "./utils/staleBuild";

interface Mishap {
  title: string;
  quips: string[];
  retryLabel: string;
}

const STALE_BUILD: Mishap = {
  title: "Fresh barrel on tap!",
  quips: [
    "We've changed the barrels since you last ordered. One refresh and you're on the new stuff.",
    "The landlord's redecorated while you were in the loo. Have a refresh and take a look.",
    "There's a new guest ale on. Your page is still drinking last week's.",
  ],
  retryLabel: "Pour me a fresh one",
};

const NOT_FOUND: Mishap = {
  title: "Wrong pub, mate",
  quips: [
    "There's no page here. Are you sure you haven't had one too many?",
    "This page left before last orders and didn't say where it was going.",
    "We've checked the beer garden, the cellar and the gents. Nothing.",
  ],
  retryLabel: "Have another look",
};

const SPILLAGE: Mishap = {
  title: "Oops! Someone's knocked the table",
  quips: [
    "Something went wrong and the page went everywhere. Nobody saw anything.",
    "The page tripped over a bar stool. It's fine, it's fine, it's absolutely fine.",
    "The barman dropped a tray. Everyone cheered. You'll want to try again.",
    "The page has gone for a lie down in the beer garden.",
  ],
  retryLabel: "Try again",
};

const SIP_REACTIONS = [
  "Steady on.",
  "That's the spirit.",
  "Debugging is thirsty work.",
  "Lovely drop.",
];

// Said as each pint in the round is finished.
const PINTS_DOWN = [
  "One down, five to go.",
  "Two down. Pace yourself.",
  "Halfway through the round.",
  "Four pints in. The error's looking friendlier already.",
  "Five! One more and you'll forget what went wrong.",
  "Six pints of Stella. Your round next, mate.",
];

const ROUND_SIZE = 6;

function pickMishap(error: unknown): Mishap {
  if (isStaleBuildError(error)) return STALE_BUILD;
  if (isRouteErrorResponse(error) && error.status === 404) return NOT_FOUND;
  return SPILLAGE;
}

function describeError(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.stack ?? `${error.name}: ${error.message}`;
  return JSON.stringify(error, undefined, 2) ?? String(error);
}

const SIPS_PER_PINT = 4;

// A Stella Artois chalice, traced from a photo and scaled so the bowl is 75
// across (as on the real 28cl glass), with the rim at y=10 and the centre line
// at x=50. A tall U-shaped bowl, widest a third of the way down; a thick stem
// holding the oval star medallion, then a waist, a bulb and a domed foot.
const BOWL =
  "M19.5 10 C17 22 12 40 12 62 C12 92 14 112 22 120 C28 127 34 131 39 131 L61 131 C66 131 72 127 78 120 C86 112 88 92 88 62 C88 40 83 22 80.5 10 Z";
const STEM =
  "M39 131 C36 136 35.5 142 36 150 C36.5 157 42 160 44 164 C44.5 167 41 169 41 172 C41 176 44 179 45 182 C40 185 26 186.5 17 188.5 C15.5 190 16 193 18 194 C30 196.5 70 196.5 82 194 C84 193 84.5 190 83 188.5 C74 186.5 60 185 55 182 C56 179 59 176 59 172 C59 169 55.5 167 56 164 C58 160 63.5 157 64 150 C64.5 142 64 136 61 131 Z";
const STAR = "M50 138 L51.6 145.2 L57 147 L51.6 148.8 L50 156 L48.4 148.8 L43 147 L48.4 145.2 Z";
const STAR_RAYS =
  "M50.0 150.8 L50.0 157.5 M51.1 150.5 L53.1 156.7 M52.1 149.7 L55.7 154.4 M52.8 148.5 L57.4 151.0 M53.0 147.0 L58.0 147.0 M52.8 145.5 L57.4 143.0 M52.1 144.3 L55.7 139.6 M51.1 143.5 L53.1 137.3 M50.0 143.2 L50.0 136.5 M48.9 143.5 L46.9 137.3 M47.9 144.3 L44.3 139.6 M47.2 145.5 L42.6 143.0 M47.0 147.0 L42.0 147.0 M47.2 148.5 L42.6 151.0 M47.9 149.7 L44.3 154.4 M48.9 150.5 L46.9 156.7";
// Gold scrollwork either side of the label's red banner.
const SCROLLS =
  "M27 38 C22 42 22 50 25 56 C27 60 31 60 31 56 C31 53 28 53 28 55 M73 38 C78 42 78 50 75 56 C73 60 69 60 69 56 C69 53 72 53 72 55";
const BEER_EMPTY_Y = 131;
const BEER_FULL_Y = 35;
const HEAD_HEIGHT = 25;
// The star etched in the bottom of the bowl, which the bubbles rise from.
const NUCLEATION_Y = 128;

function Chalice({ level }: { level: number }) {
  // Six of these share the page, so each needs its own defs ids.
  const id = useId();
  const bowlId = `${id}bowl`;
  const lagerId = `${id}lager`;
  const beerTop = BEER_EMPTY_Y - level * (BEER_EMPTY_Y - BEER_FULL_Y);

  return (
    <svg className="error-page__chalice" viewBox="0 0 100 200" aria-hidden="true">
      <defs>
        <clipPath id={bowlId}>
          <path d={BOWL} />
        </clipPath>
        <linearGradient id={lagerId} x1="0" x2="1">
          <stop offset="0" stopColor="#a8560a" />
          <stop offset="0.3" stopColor="#e9a224" />
          <stop offset="0.6" stopColor="#fcd56a" />
          <stop offset="1" stopColor="#a8560a" />
        </linearGradient>
      </defs>

      <path d={BOWL} className="error-page__glass" />
      <g clipPath={`url(#${bowlId})`}>
        <g className="error-page__lager" style={{ transform: `translateY(${beerTop}px)` }}>
          <rect y={0} width={100} height={110} fill={`url(#${lagerId})`} />
          {level > 0 && (
            <rect y={-HEAD_HEIGHT} width={100} height={HEAD_HEIGHT} className="error-page__head" />
          )}
        </g>
        {level > 0 &&
          [0, 0.5, 1, 1.5].map((delay) => (
            <circle key={delay} cx={50} r={0.9} className="error-page__fizz">
              <animate
                attributeName="cy"
                from={NUCLEATION_Y}
                to={beerTop}
                dur="2s"
                begin={`${delay}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="cx"
                values="50;51;49.5;50"
                dur="2s"
                begin={`${delay}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}
      </g>
      {/* A full pour domes up over the rim */}
      {level === 1 && (
        <path d="M19.5 10.5 C24 1.5 76 1.5 80.5 10.5 Z" className="error-page__head" />
      )}
      <path d={BOWL} className="error-page__outline" />
      <path d="M18 26 C14 44 14 74 19 98" className="error-page__shine" />
      <path d="M82 30 C85 46 85 62 83 80" className="error-page__shine error-page__shine--faint" />
      <ellipse cx={50} cy={10} rx={30.5} ry={1.8} className="error-page__rim" />

      {/* The label: a red banner between gold scrolls, under a little star */}
      <path
        d="M50 25 L51 28.5 L54.5 29.5 L51 30.5 L50 34 L49 30.5 L45.5 29.5 L49 28.5 Z"
        className="error-page__label-star"
      />
      <path d={SCROLLS} className="error-page__scrolls" />
      <path
        d="M29 40 C40 37 60 37 71 40 L71 58 C60 56 40 56 29 58 Z"
        className="error-page__label"
      />
      <text x={50} y={47} className="error-page__label-text">
        PUB
      </text>
      <text x={50} y={54.5} className="error-page__label-text">
        TRACKER
      </text>

      <path d={STEM} className="error-page__glass" />
      <ellipse cx={50} cy={147} rx={9.5} ry={12.5} className="error-page__medallion" />
      <path d={STAR_RAYS} className="error-page__star-rays" />
      <path d={STAR} className="error-page__star" />
      <path
        d="M41 172 C41 176 44 179 45 182 M59 172 C59 176 56 179 55 182"
        className="error-page__ridges"
      />
    </svg>
  );
}

export function ErrorPage() {
  const error = useRouteError();
  const mishap = pickMishap(error);
  const [quip] = useState(() => mishap.quips[Math.floor(Math.random() * mishap.quips.length)]);
  const [sips, setSips] = useState<number[]>(() => Array<number>(ROUND_SIZE).fill(0));
  const [caption, setCaption] = useState("A round of six, on the house. Get stuck in.");

  useEffect(() => {
    console.error(error);
    // Typos and stale tabs aren't bugs, so only report real spillages.
    if (pickMishap(error) === SPILLAGE) logError(error, true);
    // A tab running an old build usually just needs a reload; try that once
    // before making anyone press a button.
    if (isStaleBuildError(error)) reloadForNewBuild();
  }, [error]);

  const roundFinished = sips.every((n) => n === SIPS_PER_PINT);

  function sip(glass: number) {
    if (roundFinished) {
      setSips(Array<number>(ROUND_SIZE).fill(0));
      setCaption("Another round of six. Lovely.");
      return;
    }
    if (sips[glass] === SIPS_PER_PINT) {
      setCaption("That one's empty. Try another.");
      return;
    }
    const next = sips.map((n, i) => (i === glass ? n + 1 : n));
    setSips(next);
    if (next[glass] === SIPS_PER_PINT) {
      const finished = next.filter((n) => n === SIPS_PER_PINT).length;
      setCaption(PINTS_DOWN[finished - 1]);
    } else {
      const total = next.reduce((sum, n) => sum + n, 0);
      setCaption(SIP_REACTIONS[(total - 1) % SIP_REACTIONS.length]);
    }
  }

  return (
    <main className="error-page">
      <div className="error-page__bubbles" aria-hidden="true">
        {Array.from({ length: 12 }, (_, i) => (
          <span key={i} />
        ))}
      </div>

      <div className="error-page__round">
        <span className="error-page__tray" aria-hidden="true" />
        {sips.map((n, i) => (
          <button
            key={i}
            type="button"
            className="error-page__pint"
            style={{ animationDelay: `${-i * 0.37}s` }}
            onClick={() => sip(i)}
            aria-label={
              roundFinished
                ? "Get another round in"
                : n === SIPS_PER_PINT
                  ? `Pint ${i + 1} is empty`
                  : `Sip pint ${i + 1}`
            }
          >
            <Chalice level={(SIPS_PER_PINT - n) / SIPS_PER_PINT} />
          </button>
        ))}
      </div>
      <p className="error-page__sip" aria-live="polite">
        {caption}
      </p>

      <h1 className="error-page__title">
        {mishap.title.split(" ").map((word, i) => (
          <span key={i} style={{ animationDelay: `${i * 0.12}s` }}>
            {word}
          </span>
        ))}
      </h1>
      <p className="error-page__quip">{quip}</p>

      <div className="error-page__actions">
        <button type="button" className="btn btn-primary btn-lg" onClick={reloadPage}>
          {mishap.retryLabel}
        </button>
        <a className="btn btn-outline-light btn-lg" href="/">
          Back to the bar
        </a>
      </div>

      {mishap !== NOT_FOUND && (
        <details className="error-page__details">
          <summary>What the barman saw</summary>
          <pre>{describeError(error)}</pre>
        </details>
      )}
    </main>
  );
}
